// Libraries
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

// MUI
import { CircularProgress, Container } from '@mui/material';
import Grid from '@mui/material/Grid';

// Common
import { AAddButton } from '../common/buttons';
import useStickyState from '../common/utils/useStickyState';
import { ViewTitle } from '../common/typography';

// Core
import { IDetailedResourceText, ResourceTextApplication, systemTextApi } from '../core/resources';
import auth, { IAuthorizationResource } from '../core/authorization';
import utils from '../core/utils';

// Interfaces
import { AnelmaCultureType } from '../interfaces/types';

// API
import api from '../api/accessManagementApi';

// Store
import { useAppSelector } from '../store/hooks';

// Feature
import {
    IAccessRole,
    IAccessRoleMemberList,
    IAccessRolePermissions,
    IAccessResourceMap,
} from './IAccessRightsManagement';
import ResourcePermissions from './ResourcePermissions';
import RoleSelector from './RoleSelector';
import RoleDialog from './RoleDialog';

/** Represents access rights resource text. */
export interface IAccessRightsResourceText {
    /** Resource text's additional information that is visible to users in Resource Text UI application. */
    additionalInformation: string;
    /** In this case defaults to AnelmaAccessRights. */
    applicationName: string;
    /** E.g. AnelmaAccessRights:1120 */
    resourceTextPath: string;
    /** E.g. fi, sv-SE or en-GB */
    culture: AnelmaCultureType;
    resourceTextCode: number;
}

export default function AccessRightManagement() {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaAccessRights',
        'AnelmaGeneral',
        'AnelmaLayout',
    ]);
    const { enqueueSnackbar } = useSnackbar();
    const anelmaCulture = useAppSelector((state) => state.configuration.selectedCulture);
    const anelmaAccessRightsResourceName = 'AnelmaAccessRights';

    const [isLoaded, setIsLoaded] = useState<boolean | null>(false);
    const [roles, setRoles] = useState<IAccessRole[]>([]);
    const [resourceMap, setResourceMap] = useState<IAccessResourceMap[]>([]);
    const [resourcePermissions, setResourcePermissions] = useState<IAccessRolePermissions[]>([]);
    const [roleMembers, setRoleMembers] = useState<IAccessRoleMemberList[]>([]);
    const [selectedRoleIds, setSelectedRoleIds] = useStickyState<number[]>(
        [],
        'anelma-selected-access-rights-groups'
    );
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [editedRole, setEditedRole] = useState<IAccessRole | null>(null);
    const [accessRightsResourceCodes, setAccessRightsResourceCodes] = useState<number[]>([]);
    const [detailedAccessRightsResourceTexts, setDetailedAccessRightsResourceTexts] = useState<
        IDetailedResourceText[]
    >([]);

    const loadAccessRightsData = useCallback(async () => {
        const rolesData = await api.getRoles();
        if (!rolesData) {
            return;
        }

        setRoles(rolesData.Items);
        const roleNames = rolesData.Items.map((i) => i.name);

        const resourceMapPromise = api.getResourceMap();
        const resourcePermissionsPromise = api.getResourcePermissions(roleNames);
        const roleMembersPromise = api.getRoleMembers(roleNames);
        const detailedAccessRightsTextsPromise = systemTextApi.getDetailedList(
            [anelmaAccessRightsResourceName],
            [anelmaCulture],
            [getResourceTextEnvironmentName()]
        );

        try {
            const [
                resourceMapData,
                resourcePermissionsData,
                roleMembersData,
                detailedAccessRightsResourceTextData,
            ] = await Promise.all([
                resourceMapPromise,
                resourcePermissionsPromise,
                roleMembersPromise,
                detailedAccessRightsTextsPromise,
            ]);

            const resourceCodes = resourceMapData?.Items || [];
            setResourceMap(resourceCodes);
            setAccessRightsResourceCodes(
                utils
                    .getUniqueBy(resourceCodes, 'resourceNameTextCode')
                    .map((i) => i.resourceNameTextCode)
            );
            setResourcePermissions(resourcePermissionsData?.Items || []);
            setRoleMembers(roleMembersData?.Items || []);
            setDetailedAccessRightsResourceTexts(detailedAccessRightsResourceTextData || []);
            setIsLoaded(true);
        } catch {
            setIsLoaded(null);
        }
    }, []);

    useEffect(() => {
        loadAccessRightsData();
    }, [loadAccessRightsData]);

    function updatePermission(roleName: string, resource: IAuthorizationResource) {
        const resourcePermission = resourcePermissions.find((i) => i.role === roleName);
        if (!resourcePermission) return;
        const index = resourcePermission.permissions.findIndex(
            (i) => i.resource === resource.resource
        );
        index === -1
            ? resourcePermission.permissions.push(resource)
            : (resourcePermission.permissions[index] = resource);
        setResourcePermissions([...resourcePermissions]);
    }

    function openAddDialog() {
        setEditedRole(null);
        setDialogIsOpen(true);
    }

    function openEditDialog(role: IAccessRole) {
        setEditedRole(role);
        setDialogIsOpen(true);
    }

    function afterSaveNew(role: IAccessRole) {
        roles.push(role);
        selectedRoleIds.push(role.id as number);
        setRoles([...roles]);
        setSelectedRoleIds([...selectedRoleIds]);
        constructNewResourcePermissions(role.name);
        showSuccessMessage();
    }

    function afterSaveOld(role: IAccessRole) {
        const index = roles.findIndex((i) => i.id === role.id);
        roles[index] = role;
        setRoles([...roles]);
        showSuccessMessage();
    }

    function showSuccessMessage() {
        enqueueSnackbar(t('AnelmaGeneral:1018'), {
            variant: 'success',
        });
    }

    const constructNewResourcePermissions = (newRoleName: string) => {
        setIsLoaded(false);
        Promise.resolve(api.getResourcePermissions([newRoleName])).then(
            (response) => {
                if (response?.Items) setResourcePermissions((_) => _.concat([...response.Items]));
                setIsLoaded(true);
            },
            () => setIsLoaded(null)
        );
    };

    const getResourceTextEnvironmentName = (): string => {
        switch (window.location.host) {
            case 'localhost:44388':
            case 'snellman-anelma3-dev.mtech.fi':
                return 'Dev';
            case 'snellman-anelma3-test.mtech.fi':
                return 'Test';
            case 'snellman-anelma3-demo.mtech.fi':
                return 'Demo';
            case 'anelma3.snellman.fi':
            case 'snellman-anelma3.mtech.fi':
            case 'snellman-anelma3-production-demo.mtech.fi':
                return 'Prod';
            default:
                return 'Prod';
        }
    };

    const getAccessRightsTexts = useMemo((): IAccessRightsResourceText[] => {
        return accessRightsResourceCodes.map((code) => {
            const additionalInformation =
                detailedAccessRightsResourceTexts.find((i) => i.code === code)?.additionalInfo ||
                '';
            return {
                additionalInformation: additionalInformation,
                applicationName: anelmaAccessRightsResourceName,
                culture: anelmaCulture,
                resourceTextCode: code,
                resourceTextPath: `${anelmaAccessRightsResourceName}:${code}`,
            };
        });
    }, [accessRightsResourceCodes, t, anelmaCulture, detailedAccessRightsResourceTexts]);

    return (
        <>
            <Container data-robot-id={'app-body-management-access-rights'}>
                <ViewTitle>{t('AnelmaLayout:1006')}</ViewTitle>

                <br />

                {isLoaded ? (
                    <>
                        <Grid container>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <RoleSelector
                                    onChange={(v) => setSelectedRoleIds(v)}
                                    selectedRoleIds={selectedRoleIds}
                                    roles={roles}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                {auth.canCreateAccessRightsGroup && (
                                    <AAddButton onClick={() => openAddDialog()} type='action'>
                                        {t('AnelmaAccessRights:1003')}
                                    </AAddButton>
                                )}
                            </Grid>
                        </Grid>

                        <br />
                        <br />

                        <ResourcePermissions
                            onPermissionUpdate={(
                                roleName: string,
                                resource: IAuthorizationResource
                            ) => updatePermission(roleName, resource)}
                            openEdit={(role) => openEditDialog(role)}
                            resourceMap={resourceMap}
                            resourcePermissions={resourcePermissions}
                            roleMembers={roleMembers}
                            roles={roles.filter((i) => selectedRoleIds.includes(i.id as number))}
                            accessRightsTexts={getAccessRightsTexts}
                        />
                    </>
                ) : (
                    <Grid container>
                        <Grid size={{ xs: 12 }} style={{ textAlign: 'center' }}>
                            <CircularProgress />
                        </Grid>
                    </Grid>
                )}
            </Container>

            {dialogIsOpen ? (
                <RoleDialog
                    afterSaveFail={() => {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });
                    }}
                    afterSaveSuccess={(mode, role) =>
                        mode === 'add' ? afterSaveNew(role) : afterSaveOld(role)
                    }
                    data={editedRole}
                    onClose={() => setDialogIsOpen(false)}
                />
            ) : undefined}
        </>
    );
}
